import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, tryError } from "../lib/api";
import { getRecaptchaToken, preloadRecaptcha } from "../lib/recaptcha";
import RecaptchaNotice from "../components/RecaptchaNotice";
import { useAuth } from "../contexts/AuthContext";
import { Input } from "../components/ui/input";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Checkbox } from "../components/ui/checkbox";
import LegalDocLink from "../components/LegalDocsDialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { Loader2, ArrowRight, ArrowLeft, Sparkles } from "lucide-react";

export default function Signup() {
  const { refresh } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    business_name: "",
    contact_phone: "",
    country_code: "+90",
    owner_name: "",
    owner_email: "",
    owner_password: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const [consents, setConsents] = useState({
    membership: false,
    kvkk: false,
    privacy: false,
  });
  const allConsented = consents.membership && consents.kvkk && consents.privacy;

  useEffect(() => {
    preloadRecaptcha();
  }, []);

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    if (!allConsented) {
      setError("Devam etmek için tüm sözleşme ve metinleri onaylamanız gerekmektedir.");
      return;
    }
    setSubmitting(true);
    try {
      // Combine country code with phone number
      const fullPhone = form.contact_phone ? `${form.country_code} ${form.contact_phone}`.trim() : "";
      const submitData = {
        business_name: form.business_name,
        contact_phone: fullPhone,
        owner_name: form.owner_name,
        owner_email: form.owner_email,
        owner_password: form.owner_password,
        recaptcha_token: await getRecaptchaToken("signup"),
      };
      await api.post("/auth/signup-trial", submitData);
      await refresh();
      navigate("/");
    } catch (err) {
      setError(tryError(err) || "Kayıt başarısız.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-foreground text-background flex items-center justify-center p-6">
      <div className="w-full max-w-2xl bg-background text-foreground rounded-sm border border-border p-8 sm:p-12 grid lg:grid-cols-[1fr_1.4fr] gap-8" data-testid="signup-page">
        {/* Left side */}
        <div className="space-y-6 hidden lg:block">
          <div className="flex items-center gap-3">
            {/* Esnek Yazılım logosu — Layout/MasterLayout ile aynı görsel */}
            <img
              src="/esnek-icon.png"
              alt="Esnek Yazılım"
              className="h-10 w-10 rounded-sm object-contain shrink-0"
            />
            <div className="font-display font-black text-xl tracking-tighter">LASTİK OTELİ</div>
          </div>
          <div className="border-l-2 border-amber-500 pl-4">
            <Sparkles className="h-5 w-5 text-amber-600 mb-2" />
            <h1 className="font-display font-black text-3xl tracking-tighter leading-tight">
              Ücretsiz<br />başlayın
            </h1>
            <p className="text-sm text-muted-foreground mt-3">
              2 kullanıcı ile 30 gün ücretsiz.
            </p>
          </div>
          <ul className="space-y-2 text-sm text-muted-foreground">
            <li className="flex items-center gap-2"><span className="text-amber-600">✓</span> Anında erişim</li>
            <li className="flex items-center gap-2"><span className="text-amber-600">✓</span> İstediğiniz an yükseltin</li>
            <li className="flex items-center gap-2"><span className="text-amber-600">✓</span> Türkçe destek</li>
          </ul>
        </div>

        {/* Form */}
        <form onSubmit={onSubmit} className="space-y-4" data-testid="signup-form">
          <div>
            <div className="label-eyebrow text-amber-600">Kayıt</div>
            <h2 className="font-display font-black text-2xl sm:text-3xl tracking-tighter mt-1">
              İşletmenizi oluşturun
            </h2>
          </div>

          <div className="space-y-1.5">
            <Label className="label-eyebrow">İşletme Adı *</Label>
            <Input required value={form.business_name} onChange={(e) => setForm({ ...form, business_name: e.target.value })} className="rounded-sm h-11" data-testid="signup-business-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Telefon</Label>
            <div className="flex gap-2">
              <Select value={form.country_code} onValueChange={(value) => setForm({ ...form, country_code: value })}>
                <SelectTrigger className="rounded-sm h-11 w-28 shrink-0">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="+90">🇹🇷 +90</SelectItem>
                  <SelectItem value="+1">🇺🇸 +1</SelectItem>
                  <SelectItem value="+44">🇬🇧 +44</SelectItem>
                  <SelectItem value="+49">🇩🇪 +49</SelectItem>
                  <SelectItem value="+33">🇫🇷 +33</SelectItem>
                  <SelectItem value="+39">🇮🇹 +39</SelectItem>
                  <SelectItem value="+34">🇪🇸 +34</SelectItem>
                  <SelectItem value="+31">🇳🇱 +31</SelectItem>
                  <SelectItem value="+43">🇦🇹 +43</SelectItem>
                  <SelectItem value="+32">🇧🇪 +32</SelectItem>
                  <SelectItem value="+41">🇨🇭 +41</SelectItem>
                  <SelectItem value="+46">🇸🇪 +46</SelectItem>
                  <SelectItem value="+47">🇳🇴 +47</SelectItem>
                  <SelectItem value="+45">🇩🇰 +45</SelectItem>
                  <SelectItem value="+358">🇫🇮 +358</SelectItem>
                  <SelectItem value="+48">🇵🇱 +48</SelectItem>
                  <SelectItem value="+30">🇬🇷 +30</SelectItem>
                  <SelectItem value="+40">🇷🇴 +40</SelectItem>
                </SelectContent>
              </Select>
              <Input 
                value={form.contact_phone} 
                onChange={(e) => setForm({ ...form, contact_phone: e.target.value })} 
                className="rounded-sm h-11 flex-1" 
                placeholder="5XX XXX XX XX"
                data-testid="signup-phone" 
              />
            </div>
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Ad Soyad *</Label>
            <Input required value={form.owner_name} onChange={(e) => setForm({ ...form, owner_name: e.target.value })} className="rounded-sm h-11" data-testid="signup-name" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">E-posta *</Label>
            <Input required type="email" autoComplete="username" value={form.owner_email} onChange={(e) => setForm({ ...form, owner_email: e.target.value })} className="rounded-sm h-11" data-testid="signup-email" />
          </div>
          <div className="space-y-1.5">
            <Label className="label-eyebrow">Şifre *</Label>
            <Input required minLength={6} type="password" autoComplete="new-password" value={form.owner_password} onChange={(e) => setForm({ ...form, owner_password: e.target.value })} className="rounded-sm h-11" placeholder="En az 6 karakter" data-testid="signup-password" />
          </div>

          {/* Zorunlu onay metinleri */}
          <div className="space-y-3 pt-1">
            <div className="flex items-start gap-2">
              <Checkbox
                id="consent-membership"
                checked={consents.membership}
                onCheckedChange={(v) => setConsents((c) => ({ ...c, membership: v === true }))}
                className="mt-0.5"
                data-testid="consent-membership"
              />
              <Label htmlFor="consent-membership" className="text-xs leading-relaxed font-normal text-muted-foreground cursor-pointer">
                <LegalDocLink doc="membership">Üyelik ve Kullanım Sözleşmesi</LegalDocLink>
                ’ni okudum, kabul ediyorum.
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="consent-kvkk"
                checked={consents.kvkk}
                onCheckedChange={(v) => setConsents((c) => ({ ...c, kvkk: v === true }))}
                className="mt-0.5"
                data-testid="consent-kvkk"
              />
              <Label htmlFor="consent-kvkk" className="text-xs leading-relaxed font-normal text-muted-foreground cursor-pointer">
                <LegalDocLink doc="kvkk">KVKK Aydınlatma Metni</LegalDocLink>
                ’ni okudum, kabul ediyorum.
              </Label>
            </div>
            <div className="flex items-start gap-2">
              <Checkbox
                id="consent-privacy"
                checked={consents.privacy}
                onCheckedChange={(v) => setConsents((c) => ({ ...c, privacy: v === true }))}
                className="mt-0.5"
                data-testid="consent-privacy"
              />
              <Label htmlFor="consent-privacy" className="text-xs leading-relaxed font-normal text-muted-foreground cursor-pointer">
                <LegalDocLink doc="privacy">Gizlilik Politikası ve Çerez Politikası</LegalDocLink>
                ’nı okudum, kabul ediyorum.
              </Label>
            </div>
          </div>

          {error && (
            <div className="border-l-2 border-destructive bg-destructive/10 px-4 py-3 text-sm text-destructive" data-testid="signup-error">
              {error}
            </div>
          )}

          <Button type="submit" disabled={submitting || !allConsented} className="w-full h-12 rounded-sm bg-foreground text-background hover:bg-foreground/90 group disabled:opacity-50" data-testid="signup-submit">
            {submitting ? <Loader2 className="h-4 w-4 animate-spin" /> : (
              <span className="flex items-center justify-center gap-2 font-semibold">Hesap Oluştur <ArrowRight className="h-4 w-4 group-hover:translate-x-1 transition-transform" /></span>
            )}
          </Button>

          <Link to="/login" className="flex items-center justify-center gap-1 text-sm text-muted-foreground hover:text-foreground" data-testid="signup-to-login">
            <ArrowLeft className="h-3.5 w-3.5" /> Zaten hesabım var
          </Link>

          {/* Rozet gizlendiği için Google'ın istediği bilgilendirme metni */}
          <RecaptchaNotice className="text-center" />
        </form>
      </div>
    </div>
  );
}
